import { create } from 'zustand';
import { fetchLocationConfig } from '@/lib/api/locationConfig';
import type { UnitSystem } from '@/lib/api/locationConfig';
import { logger } from '@/lib/logger';

const CACHE_TTL_MS = 60 * 1000;

export interface LocationConfig {
  latitude: number;
  longitude: number;
  zoom: number;
}

interface LocationConfigStore {
  config: LocationConfig | null;
  unitSystem: UnitSystem;
  error: Error | null;
  isLoading: boolean;
  lastFetched: Date | null;
  cachedTenant: string | null;
  fetchLocationConfig: (tenant?: string) => Promise<void>;
  clearError: () => void;
}

const initialState = {
  config: null,
  unitSystem: 'imperial' as UnitSystem,
  error: null,
  isLoading: false,
  lastFetched: null,
  cachedTenant: null,
};

export const useTenantMapConfigStore = create<LocationConfigStore>((set, get) => ({
  ...initialState,

  fetchLocationConfig: async (tenant?: string) => {
    const { config, lastFetched, cachedTenant, isLoading } = get();

    if (isLoading) {
      return;
    }

    const isTenantMatch = cachedTenant === tenant;
    const isRecentlyFetched = lastFetched && Date.now() - lastFetched.getTime() < CACHE_TTL_MS;

    if (config && isTenantMatch && isRecentlyFetched) {
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const locationConfig = await fetchLocationConfig(tenant);

      set({
        config: locationConfig,
        unitSystem: locationConfig.unitSystem ?? 'imperial',
        lastFetched: new Date(),
        cachedTenant: tenant || null,
        error: null,
        isLoading: false,
      });
    } catch (error) {
      logger.error('Failed to fetch location config', 'LocationConfigStore', error);
      set({
        config: null,
        unitSystem: 'imperial',
        error: error instanceof Error ? error : new Error('Unknown error'),
        isLoading: false,
      });
    }
  },

  clearError: () => set({ error: null }),
}));
